import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { AttachmentKind, DocumentsCriteria, DocumentsState, DocumentsStore, PAGE_SIZE } from './documents.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from "@angular/router";
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';
import { PagerEntry, pagerPages } from '../bom-list/pager-pages';
import { BomAttachmentListItemDto } from './models/bom-attachment-list-item.dto';
import { formatDayMonthYear } from '../../../utils/date-only';
import { plural } from '../../../utils/plural';

// one decimal and the decimal comma of 0111: "820,4 KB"
const SIZE_FORMAT = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false });

const ATTACHMENT_KINDS: AttachmentKind[] = ['image', 'pdf', 'spreadsheet', 'text'];

// one row's kind, in the Typ column
const KIND_LABELS: Record<AttachmentKind, string> = {
  image: 'Zdjęcie',
  pdf: 'PDF',
  spreadsheet: 'Arkusz',
  text: 'Tekst',
};

interface TypeFilterOption {
  type: AttachmentKind | undefined;
  label: string;
}

// the filter names the bucket, so the plural where there is one
const TYPE_FILTER_OPTIONS: TypeFilterOption[] = [
  { type: undefined, label: 'Wszystkie' },
  { type: 'image', label: 'Zdjęcia' },
  { type: 'pdf', label: 'PDF' },
  { type: 'spreadsheet', label: 'Arkusze' },
  { type: 'text', label: 'Tekst' },
];

// The API's KindsByContentType (UploadBomAttachment) read the other way, as
// the ZAŁĄCZNIKI card's attachmentGroup reads its tabs: whatever is left of
// the allowed types is a sheet (.xlsx, .xls, .csv)
function attachmentKind(contentType: string): AttachmentKind {
  if (contentType.startsWith('image/')) {
    return 'image';
  }

  if (contentType === 'application/pdf') {
    return 'pdf';
  }

  if (contentType === 'text/plain') {
    return 'text';
  }

  return 'spreadsheet';
}

// The list's criteria live in the query string (0092). Defaults are never
// written: /documents with no parameters is Wszystkie, page 1, no search, so
// the sidenav's link resets the list. Absent type is Wszystkie here, so it
// needs no spelling of its own; anything unknown reads as absent and is never
// forwarded to the API, which would answer 400
function parseCriteria(params: ParamMap): DocumentsCriteria {
  const page = Number(params.get('page'));

  return {
    search: (params.get('search') ?? '').trim(),
    typeFilter: ATTACHMENT_KINDS.find(kind => kind === params.get('type')),
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

function pageParam(page: number): number | null {
  return page >= 2 ? page : null;
}

// null removes the parameter under queryParamsHandling: 'merge'
function criteriaParams(criteria: DocumentsCriteria): Params {
  return {
    search: criteria.search || null,
    type: criteria.typeFilter ?? null,
    page: pageParam(criteria.page),
  };
}

function sameCriteria(a: DocumentsCriteria, b: DocumentsCriteria): boolean {
  return a.search === b.search
    && a.typeFilter === b.typeFilter
    && a.page === b.page;
}

@Component({
  selector: 'documents-page',
  templateUrl: './documents.page.html',
  styleUrl: './documents.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, DropdownComponent, ItemPhotoComponent]
})
export class DocumentsPage implements OnInit, OnDestroy {

  readonly state$: Observable<DocumentsState>;
  readonly kindLabels = KIND_LABELS;
  readonly pageSize = PAGE_SIZE;

  // the list's shadow while it loads (0128): five rows at the row's height
  readonly shadowRows = [0, 1, 2, 3, 4];

  private readonly searchTerm$ = new Subject<string>();
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('search');
  private readonly listTable = viewChild<ElementRef<HTMLDivElement>>('listTable');
  private readonly urlSearch = signal('');
  // The last term the box itself wrote to the URL: its echo must not
  // overwrite what the user has typed since
  private writtenSearch: string | undefined;

  constructor(
    private store: DocumentsStore,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // The box shows the URL's search: on a shared link's first render, and
    // when the URL changes under the live page (the sidenav's Dokumentacja
    // clears it). It stays an uncontrolled input, a [value] binding would set
    // it back to an older term while the user is still typing
    effect(() => {
      const input = this.searchInput();
      const term = this.urlSearch();

      if (input && term !== this.writtenSearch) {
        input.nativeElement.value = term;
      }
    });
  }

  ngOnInit(): void {
    // The URL is the single source of truth for the criteria, as on /bom:
    // gestures write it and this one stream reads it. switchMap drops the
    // in-flight request when newer criteria arrive
    this.route.queryParamMap
      .pipe(
        map(params => {
          const criteria = parseCriteria(params);

          this.urlSearch.set(criteria.search);
          this.normalizeUrl(params, criteria);

          return criteria;
        }),
        // the normalized URL parses to the same criteria, no second request
        distinctUntilChanged(sameCriteria),
        switchMap(criteria => {
          // a new page is read from its top
          const pageChanged = criteria.page !== this.store.state.page;

          return this.store.load(criteria).pipe(tap(() => {
            this.clampPage();

            if (pageChanged) {
              this.scrollToTop();
            }
          }));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // No distinctUntilChanged here: after the sidenav cleared the box,
    // retyping the previous term must be written again
    this.searchTerm$
      .pipe(
        debounceTime(300),
        map(term => term.trim()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => {
        this.writtenSearch = term;
        // a new question starts at its first answer
        this.navigate({ search: term || null, page: null });
      });
  }

  onSearchInput(term: string): void {
    this.searchTerm$.next(term);
  }

  searchTypeOptions = (searchTerm: string) => of(
    TYPE_FILTER_OPTIONS.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  formatTypeOption = (option: TypeFilterOption) => option.label;

  typeFilterLabel(type: AttachmentKind | undefined): string {
    return TYPE_FILTER_OPTIONS.find(option => option.type === type)!.label;
  }

  onTypeSelected(event: ItemSelectedEvent<TypeFilterOption>): void {
    // Enter on a raw search term selects nothing — the filter only has
    // its five fixed options
    if (!event.item) {
      return;
    }

    this.navigate({ type: event.item.type ?? null, page: null });
  }

  kindOf(attachment: BomAttachmentListItemDto): AttachmentKind {
    return attachmentKind(attachment.contentType);
  }

  contentUrl(attachment: BomAttachmentListItemDto): string {
    return this.store.contentUrl(attachment.id);
  }

  // "128 plików", the count line
  fileCountLabel(count: number): string {
    return `${count} ${plural(count, 'plik', 'pliki', 'plików')}`;
  }

  // "820,4 KB", "12,3 MB" — KB up to a megabyte, MB from there
  formatSize(sizeBytes: number): string {
    const kilobytes = sizeBytes / 1024;

    if (kilobytes < 1024) {
      return `${SIZE_FORMAT.format(kilobytes)} KB`;
    }

    return `${SIZE_FORMAT.format(kilobytes / 1024)} MB`;
  }

  formatCreatedAt(attachment: BomAttachmentListItemDto): string {
    return formatDayMonthYear(attachment.createdAt.slice(0, 10));
  }

  pagerPages(state: DocumentsState): PagerEntry[] {
    return pagerPages(state.page, this.pageCount(state));
  }

  pageCount(state: DocumentsState): number {
    return Math.ceil(state.totalCount / PAGE_SIZE);
  }

  // the pager's links merge this into the current criteria
  pageParams(page: number): Params {
    return { page: pageParam(page) };
  }

  // A stale or pasted link (bad type or page) is rewritten to the view it
  // shows, so the address always says what is on screen
  private normalizeUrl(params: ParamMap, criteria: DocumentsCriteria): void {
    const canonical = criteriaParams(criteria);
    const differs = Object.entries(canonical)
      .some(([key, value]) => params.get(key) !== (value === null ? null : String(value)));

    if (differs) {
      this.navigate(canonical);
    }
  }

  // A page past the end (files deleted, the link is a week old) moves to the
  // last page that exists; an empty result to page 1
  private clampPage(): void {
    const state = this.store.state;
    const lastPage = Math.max(1, this.pageCount(state));

    if (state.page > lastPage) {
      this.navigate({ page: pageParam(lastPage) });
    }
  }

  // Every list gesture replaces the history entry: the list is one entry,
  // whatever was typed or paged, so Wstecz always leaves it
  private navigate(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  // The table is the scroll box (0103), the page itself never scrolls
  private scrollToTop(): void {
    const table = this.listTable();

    if (table) {
      table.nativeElement.scrollTop = 0;
    }
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}
